
import { Menu, Plus, User } from "lucide-react";
import { icon32Base64 } from "@/assets/icon32.base64";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { useState, useEffect, useRef } from "react";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import AppUpdateBanner from "@/components/update/AppUpdateBanner";
import { openReviewPage } from "@/services/app/review.service";
import logger from "@/utils/logger";
import VisualDebugger from "@/components/debug/VisualDebugger";
import useDebugPreferences from "@/hooks/useDebugPreferences";

interface HeaderProps {
  onMenuClick: () => void;
  onAddClick?: () => void;
  onHelpClick?: () => void;
}

const Header = ({ onMenuClick, onAddClick, onHelpClick }: HeaderProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { theme } = useThemeConfig();
  const headerRef = useRef<HTMLElement>(null);
  const { visualDebuggerEnabled, queryOverrideActive } = useDebugPreferences();
  const showVisualDebugger = visualDebuggerEnabled || queryOverrideActive;

  // Invece di eseguire direttamente il logout qui, reindirizziamo
  // l'utente alla pagina dedicata che gestisce la disconnessione.
  const handleLogout = () => {
    setIsLoggingOut(true);
    logger.log("Navigating to logout page");
    navigate('/logout');
  };

  const handleLoginClick = () => {
    logger.log("Login button clicked, navigating to login page");
    navigate('/login');
  };

  return (
    <>
    <header
      ref={headerRef}
      role="banner"
      aria-label="Intestazione principale"
      className="sticky top-0 z-50 w-full"
      style={{
        background: theme.headerGradient ?? theme.backgroundColor,
        color: theme.textColor,
        boxShadow: theme.boxShadow,
        transition: `all ${theme.transitionSpeed} ease`,
      }}
    >
      <div
        className="container px-4 flex items-end justify-between relative"
        style={{
          // Extra space avoids the status bar or notch covering the header
          paddingTop: `calc(var(--safe-area-top) + 25px)`,
          paddingBottom: '0.75rem',
        }}
      >
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            aria-label="Apri menu"
            className="mr-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
            style={{
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding,
              transition: `all ${theme.transitionSpeed} ease`,
              color: theme.textPrimaryColor,
            }}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1
            className="font-semibold text-sm"
            style={{ color: theme.textPrimaryColor || theme.textColor }}
          >
          Ceralia - Spesa Semplice
          </h1>
        </div>
        
        <div className="flex items-center space-x-0">          
          {onAddClick && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onAddClick}
              aria-label="Aggiungi"
              className="tutorial-target-homepage-add-list-button focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
              style={{
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                transition: `all ${theme.transitionSpeed} ease`,
                color: theme.textPrimaryColor,
              }}
            >
              <Plus className="h-5 w-5" />
            </Button>
          )}
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Apri menu utente"
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2"
                  style={{
                    borderRadius: theme.buttonBorderRadius,
                    padding: theme.buttonPadding,
                    transition: `all ${theme.transitionSpeed} ease`,
                    color: theme.textPrimaryColor,
                  }}
                >
                  <User className="h-4 w-4" />
                  <span className="sr-only sm:not-sr-only">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                style={{
                  borderRadius: theme.borderRadius,
                  backgroundColor: theme.backgroundColor,
                  color: theme.sectionTitleColor,
                }}
              >
                <DropdownMenuLabel>Il mio account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    Il mio profilo
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    Impostazioni
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/premium" className="cursor-pointer">
                    Premium
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/devices" className="cursor-pointer">
                    Gestisci dispositivi
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openReviewPage}>
                  Lascia una recensione
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-500 cursor-pointer"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? "Uscita..." : "Esci"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              onClick={handleLoginClick} 
              variant="outline" 
              size="sm"
              style={{
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                fontWeight: theme.fontWeightNormal,
                transition: `all ${theme.transitionSpeed} ease`,
              }}
            >
              Accedi
            </Button>
          )}
          <div className="ml-3">
            <img
              src={icon32Base64}
              alt="Logo Ceralia"
              className="h-6 w-6 object-contain"
              draggable={false}
            />
          </div>

        </div>
      </div>
    </header>
    <div aria-hidden="true" style={{ height: "var(--header-height)" }} />
    {showVisualDebugger && <VisualDebugger />}
    <AppUpdateBanner />
    </>
  );
};

export default Header;
